import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { Form, Input, Select } from 'antd'
import { useAtom } from 'jotai'
import { useCallback, useEffect, type FC, type FormEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  LABELS,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  type CreateShipmentCategoriesDto,
  type ShipmentCategoryEntity
} from '@/libs/api'
import { COLOR_TYPE } from '@/libs/api/types/'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { createShipmentCategories } from '../../api'
import {
  CreateShipmentCategoriesDtoSchema,
  initialShipmentCategoriesFormValue
} from '../../models'

type Props = {
  tenantKey: string
  onSuccess: (shipmentCategories: ShipmentCategoryEntity) => Promise<void>
  onCancel: () => void
}

export const ShipmentCategoryCreateModalContent: FC<Props> = ({
  tenantKey,
  onSuccess,
  onCancel
}) => {
  const {
    control,
    setFocus,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<CreateShipmentCategoriesDto>({
    defaultValues: {
      ...initialShipmentCategoriesFormValue
    },
    resolver: valibotResolver(CreateShipmentCategoriesDtoSchema)
  })

  const router = useRouter()
  const [selectedDcId] = useAtom(selectedDcIdAtom)

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      // formが入れ子になっている場合の対応
      // - submitイベントの親要素への伝播を止める
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = await createShipmentCategories({
            tenantKey,
            dto: {
              ...formValue,
              dcId: selectedDcId ?? undefined
            }
          })
          onSuccess(data)
          toast.success(SUCCESS_MESSAGES.REGISTERED_SHIPMENT_CATEGORY)
        } catch (error) {
          toastApiErrorMessage(
            error,
            ERROR_MESSAGES.FAILED_TO_REGISTER_SHIPMENT_CATEGORY
          )
        }
      })(event)
    },
    [tenantKey, onSuccess, handleSubmit, selectedDcId]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  useEffect(() => {
    setFocus('code')
  }, [setFocus])

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[230px]" />
            <col className="w-[100px]" />
            <col className="w-[230px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.SHIPMENT_CATEGORY_CODE}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'code'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.SHIPMENT_CATEGORY_CODE}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="name">
                  {LABELS.SHIPMENT_CATEGORY_NAME}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'name'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.SHIPMENT_CATEGORY_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="displayColor">
                  {LABELS.SHIPMENT_CATEGORY_COLOR}
                </label>
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd className="align-top">
                <Controller
                  name={'displayColor'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Select
                        {...field}
                        className="w-full"
                        options={COLOR_TYPE.map((color) => ({
                          value: color,
                          label: (
                            <div
                              className="h-5 w-full rounded border border-[#d9d9d9]"
                              style={{ backgroundColor: color }}
                            />
                          )
                        }))}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.REGISTER}
        </Button>
      </ModalFooter>
    </form>
  )
}
